import {
  differenceInSeconds,
  format,
  isToday,
  isYesterday,
  parse,
  startOfTomorrow,
} from 'date-fns';

function createDateStamp(): string {
  return format(new Date(), 'yyyy-MM-dd');
}

function wasItYesterdayUtil(date_old: string): boolean {
  return isYesterday(parseDateStamp(date_old));
}

function wasItTodayUtil(stamp: string): boolean {
  return isToday(parseDateStamp(stamp));
}

function parseDateStamp(stamp: string): Date {
  return parse(stamp, 'yyyy-MM-dd', new Date());
}

function getSecondsToNextDay(): number {
  return differenceInSeconds(startOfTomorrow(), new Date());
}

function secondsToHuman(seconds: number): string {
  const s = seconds % 60;
  const m = Math.floor(seconds / 60) % 60;
  const h = Math.floor(seconds / 3600);

  const pad = (e: number) => e.toString().padStart(2, '0');
  if (h === 0) {
    return `${pad(m)}:${pad(s)}`;
  }
  return `${pad(h)}:${pad(m)}:${pad(s)}`;
}

const noop = () => {
  return;
};

const LegacyCopy = (text: string) => {
  const dummy = document.createElement('textarea');
  document.body.appendChild(dummy);
  dummy.value = text;
  dummy.select();
  document.execCommand('copy');
  document.body.removeChild(dummy);
};

const copyToClipboard = async (text: string) => {
  if (navigator.clipboard) {
    await navigator.clipboard.writeText(text);
  } else {
    LegacyCopy(text);
  }
};

interface NativeShareOptions {
  title?: string;
  text: string;
  url?: string;
}

const canNativeShare = (): boolean => {
  try {
    return (
      typeof window !== 'undefined' &&
      window.isSecureContext !== false &&
      typeof navigator !== 'undefined' &&
      typeof navigator.share === 'function'
    );
  } catch {
    return false;
  }
};

const nativeShare = (opts: NativeShareOptions): Promise<'shared' | 'copied'> => {
  const fullText = opts.url
    ? opts.text + '\n\n' + opts.url
    : opts.text;

  if (canNativeShare()) {
    return navigator.share({ text: fullText }).then(
      () => 'shared' as const,
      (e: unknown) => {
        if (e instanceof DOMException && (e.name === 'AbortError' || e.name === 'NotAllowedError')) {
          return 'copied' as const;
        }
        return copyToClipboard(fullText).then(() => 'copied' as const);
      },
    );
  }

  // Fallback: copy to clipboard and return 'copied' so callers can show feedback
  return copyToClipboard(fullText).then(() => 'copied' as const);
};

/** @deprecated Use nativeShare() instead */
const shareOrCopy = async (text: string, url?: string): Promise<'shared' | 'copied'> => {
  return nativeShare({ text, url });
};

const generateStarBar = (currentPoints: number, maxPoints: number): string => {
  if (maxPoints <= 0) return '☆☆☆☆☆';
  let filled = 0;
  if (currentPoints > 0) filled = 1;
  if (currentPoints >= maxPoints * 0.25) filled = 2;
  if (currentPoints >= maxPoints * 0.5) filled = 3;
  if (currentPoints >= maxPoints * 0.75) filled = 4;
  if (currentPoints >= maxPoints) filled = 5;
  return '★'.repeat(filled) + '☆'.repeat(5 - filled);
};

export {
  copyToClipboard,
  shareOrCopy,
  nativeShare,
  generateStarBar,
  wasItYesterdayUtil,
  wasItTodayUtil,
  createDateStamp,
  noop,
  getSecondsToNextDay,
  secondsToHuman,
};
